import { useState, useEffect, useCallback } from 'react'
import {
  getSystemAirdropHistory,
  executeSystemAirdrop,
  type SystemAirdropHistoryResponse,
  type ExecuteAirdropRequest,
  type ExecuteAirdropResponse,
} from '../lib/api'

interface UseSystemAirdropHistoryResult {
  data: SystemAirdropHistoryResponse | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook for fetching system airdrop history with pagination
 */
export function useSystemAirdropHistory(params?: {
  limit?: number
  offset?: number
}): UseSystemAirdropHistoryResult {
  const [data, setData] = useState<SystemAirdropHistoryResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getSystemAirdropHistory(params)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch airdrop history')
    } finally {
      setLoading(false)
    }
  }, [params?.limit, params?.offset])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

interface UseExecuteAirdropResult {
  execute: (data: ExecuteAirdropRequest) => Promise<ExecuteAirdropResponse>
  loading: boolean
  error: string | null
  result: ExecuteAirdropResponse | null
  reset: () => void
}

/**
 * Hook for executing system airdrops
 */
export function useExecuteAirdrop(): UseExecuteAirdropResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ExecuteAirdropResponse | null>(null)

  const execute = useCallback(async (data: ExecuteAirdropRequest): Promise<ExecuteAirdropResponse> => {
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const response = await executeSystemAirdrop(data)
      setResult(response)
      return response
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to execute airdrop'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const reset = useCallback(() => {
    setError(null)
    setResult(null)
  }, [])

  return { execute, loading, error, result, reset }
}
